const { datadogLogs } = await import('@datadog/browser-logs');
const { datadogRum } = await import('@datadog/browser-rum');
import { ReactNode, Component, isValidElement, PropsWithChildren } from 'react';

type FallbackRenderer = (error: Error) => React.ReactNode;

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
  prevScope?: string;
}

interface ErrorBoundaryProps extends PropsWithChildren {
  fallback: ReactNode | FallbackRenderer;
  scope?: string;
}

/**
 * ErrorBoundary component sends enriched errors to RUM.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  static defaultProps = {
    scope: 'error-boundary',
  };

  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, prevScope: props.scope };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    // Update state so the next render will show the fallback UI.
    return { hasError: true, error };
  }

  static getDerivedStateFromProps(
    props: ErrorBoundaryProps,
    state: ErrorBoundaryState
  ): ErrorBoundaryState {
    if (state.prevScope !== props.scope) {
      return {
        hasError: false,
        error: undefined,
        prevScope: props.scope,
      };
    }

    return state;
  }

  componentDidCatch(error: Error): void {
    datadogRum.addError(error, {
      scope: this.props.scope,
    });
    datadogLogs.logger.error(error.message, {
      scope: this.props.scope,
      error: { kind: error.name, stack: error.stack },
    });
  }

  render(): ReactNode {
    const { hasError, error } = this.state;
    const { fallback } = this.props;

    if (!hasError || !error) {
      return this.props.children;
    }

    if (isValidElement(fallback) || typeof fallback === 'string') {
      return fallback;
    } else if (typeof fallback === 'function') {
      return fallback(error);
    }

    return null;
  }
}
